import React from "react";
import { useLocation } from "react-router";
import { RotateCcw, SlidersHorizontal } from "lucide-react";

import ConfirmationModal from "./ConfirmationModal.jsx";
import { fetchWithSupabaseAuth } from "../lib/supabaseClient.js";
import { endPreferencesTarget, preferencesResetPage, readPreferencesTarget, resetPreferencesPage } from "../utils/preferencesDelegation.js";

// Visible seulement chez l'Admin qui règle les préférences d'un autre utilisateur, sur toutes les pages.
export default function PreferencesDelegationBanner() {
  const [target] = React.useState(readPreferencesTarget);
  const location = useLocation();
  const [confirming, setConfirming] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  if (!target) return null;
  const resetPage = preferencesResetPage(location.pathname, location.search);
  const name = target.email || target.userId;
  const reset = async () => {
    setBusy(true);
    setError("");
    try {
      await resetPreferencesPage(confirming, { fetchImpl: fetchWithSupabaseAuth });
    } catch (resetError) {
      setError(resetError.message);
      setBusy(false);
    }
  };
  const close = () => { if (!busy) { setConfirming(null); setError(""); } };
  return (
    <div role="status" data-preferences-delegation-banner=""
      className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 bg-[#b45309] px-3.5 py-1.5 text-[0.8rem] leading-5 text-white">
      <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-0.5">
        <SlidersHorizontal className="h-4 w-4 shrink-0" aria-hidden="true" />
        <strong className="min-w-0 break-all">Réglages de {name}</strong>
        {target.roleLabel ? (
          <span className="rounded-[4px] border border-white/60 px-1.5 py-0.5 text-[0.62rem] font-semibold uppercase tracking-[0.08em]">{target.roleLabel}</span>
        ) : null}
        <span className="opacity-90">Préférences, colonnes et disposition enregistrées pour cet utilisateur · Données et droits : les vôtres</span>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {resetPage ? (
          <button type="button" onClick={() => setConfirming(resetPage)} title={`Remettre les réglages de ${resetPage.label} comme à sa première visite`}
            className="inline-flex h-6 items-center gap-1.5 rounded-[4px] border border-white/80 px-2.5 text-[0.75rem] font-semibold text-white hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white">
            <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
            Réinitialiser {resetPage.label}
          </button>
        ) : null}
        <button type="button" onClick={() => endPreferencesTarget()}
          className="h-6 shrink-0 rounded-[4px] border border-white bg-white px-3 text-[0.75rem] font-bold text-[#7c2d12] hover:bg-white/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white">
          Terminer
        </button>
      </div>
      {confirming ? (
        <ConfirmationModal eyebrow="Réglages de l'utilisateur" title={`Réinitialiser ${confirming.label} ?`} tone="warning" panelSize="narrow"
          description={`Les réglages de ${confirming.label} reviennent comme à la première visite de ${name}.`}
          impactItems={[
            "Affichage, colonnes, tris, filtres et onglets de cette page reprennent leurs valeurs par défaut.",
            "La journée ou la période ouverte, le menu de gauche et les autres pages ne changent pas.",
          ]}
          errorMessage={error} cancelLabel="Annuler" confirmLabel="Réinitialiser" isBusy={busy}
          onCancel={close} onClose={close} onConfirm={reset} />
      ) : null}
    </div>
  );
}
